/**
 * QuickLinks — a thin horizontal "section-aware shortcut strip" rendered at
 * the top of every authenticated page.
 *
 * Behaves like a mini-sidebar: pulls the current route's section (Home, AI Hub,
 * Accounting Hub, Library, Learn, Admin) from a static map and renders the
 * section title + every nav item in that section as compact icon tiles. The
 * tile for the current route is highlighted.
 *
 * Visual: glass-style tiles with a subtle gradient + soft elevation that lifts
 * on hover — the "3D-ish" look the user asked for while staying readable.
 *
 * Order of items mirrors the sidebar exactly so nothing surprises the user.
 */

import { useMemo, useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  Sparkles, MessageSquare, MessageCircle, Rocket, BrainCircuit, Zap, BarChart3,
  LayoutDashboard, TrendingUp, Landmark, ScanLine, FileText, Users, Receipt,
  BookOpen, Map, Repeat, BookMarked, History, ArrowRightLeft,
  FolderTree, ClipboardList, Timer, GraduationCap, Trophy, Medal,
  Settings, Shield, Building2, ChevronUp, ChevronDown, Inbox,
} from 'lucide-react';
import { cn } from '../lib/utils';

// Mirror of AppLayout.navSections — kept tight and icon-only here. Update both
// in tandem when adding/removing sidebar items.
const SECTIONS = [
  {
    key: 'home',
    title: 'Home',
    paths: ['/ai-hub', '/all-conversations', '/documents', '/inbox', '/client-replies', '/messages'],
    items: [
      { path: '/ai-hub',            label: 'AI Studio',         icon: Sparkles },
      { path: '/all-conversations', label: 'All Conversations', icon: MessageSquare },
      { path: '/documents',         label: 'Documents',         icon: FileText },
      { path: '/inbox',             label: 'Client Inbox',      icon: Inbox },
    ],
  },
  {
    key: 'ai-hub',
    title: 'AI Hub',
    paths: ['/ai-command', '/cfo-buck', '/ai-buck', '/reconcile-buck', '/invoice-ocr', '/statement-importer', '/loan-qualification'],
    items: [
      { path: '/ai-command',        label: 'Prompt Launchpad',  icon: Rocket },
      { path: '/cfo-buck',          label: 'CFO Buck',          icon: BrainCircuit },
      { path: '/ai-buck',           label: 'Auto-Categorizer',  icon: Sparkles },
      { path: '/reconcile-buck',    label: 'Smart Reconcile',    icon: Zap },
      { path: '/invoice-ocr',       label: 'Doc Scanner',       icon: ScanLine },
      { path: '/statement-importer',label: 'Batch Importer',    icon: FileText },
      { path: '/loan-qualification',label: 'Loan Qualification',icon: BarChart3 },
    ],
  },
  {
    key: 'accounting-hub',
    title: 'Accounting Hub',
    paths: [
      '/dashboard', '/banking',
      '/invoices', '/contacts', '/bills', '/accounts', '/tax-mapper',
      '/journal-entries', '/recurring-entries', '/ledger', '/reports', '/audit',
      '/switch-from-quickbooks',
    ],
    items: [
      { path: '/dashboard',          label: 'Dashboard',     icon: LayoutDashboard },
      { path: '/banking',            label: 'Banking',       icon: Landmark },
      { path: '/invoices',           label: 'Invoices',      icon: FileText },
      { path: '/contacts?type=customer', label: 'Customers', icon: Users },
      { path: '/bills',              label: 'Bills',         icon: Receipt },
      { path: '/contacts?type=vendor',   label: 'Vendors',   icon: Users },
      { path: '/accounts',           label: 'Chart of Accts',icon: BookOpen },
      { path: '/tax-mapper',         label: 'Tax Mapper',    icon: Map },
      { path: '/journal-entries',    label: 'Journal',       icon: FileText },
      { path: '/recurring-entries',  label: 'Recurring',     icon: Repeat },
      { path: '/ledger',             label: 'Ledger',        icon: BookMarked },
      { path: '/reports',            label: 'Reports',       icon: BarChart3 },
      { path: '/audit',              label: 'Audit Log',     icon: History },
      { path: '/switch-from-quickbooks', label: 'Switch QB', icon: ArrowRightLeft },
    ],
  },
  {
    key: 'productivity',
    title: 'Productivity',
    paths: ['/tasks', '/time-tracking'],
    items: [
      { path: '/tasks',         label: 'Tasks',         icon: ClipboardList },
      { path: '/time-tracking', label: 'Time Tracking', icon: Timer },
    ],
  },
  {
    key: 'learn',
    title: 'Learn',
    paths: ['/university', '/guided-tours', '/achievements', '/leaderboard'],
    items: [
      { path: '/university',   label: 'University',   icon: GraduationCap },
      { path: '/guided-tours', label: 'Guided Tours', icon: Map },
      { path: '/achievements', label: 'Achievements', icon: Trophy },
      { path: '/leaderboard',  label: 'Leaderboard',  icon: Medal },
    ],
  },
  {
    key: 'admin',
    title: 'Admin',
    paths: ['/admin', '/settings'],
    items: [
      { path: '/admin',    label: 'Admin Panel', icon: Shield },
      { path: '/settings', label: 'Settings',    icon: Settings },
    ],
  },
];

const HIDDEN_PATHS = new Set([
  // Pages where the QuickLinks strip would clash with their own custom hero
  // header (login, public, setup wizard, owner-admin internal pages, etc.).
  '/login', '/legacy-login', '/legacy-landing',
]);

export default function QuickLinks() {
  const { pathname, search } = useLocation();
  // Collapsed state is shared across all sections via a single localStorage
  // key so the user's preference travels with them as they navigate.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      // Collapsed by DEFAULT for a cleaner first impression; only expanded if
      // the user explicitly opened it before (stored '0').
      return localStorage.getItem('quick_links_collapsed_v1') !== '0';
    }
    catch { return true; }
  });

  useEffect(() => {
    try { localStorage.setItem('quick_links_collapsed_v1', collapsed ? '1' : '0'); }
    catch { /* */ }
  }, [collapsed]);

  const activeSection = useMemo(() => {
    // Choose the section whose `paths` contains the longest prefix match for
    // the current pathname. Defaults to no-render when nothing matches.
    let best = null;
    let bestLen = 0;
    for (const sec of SECTIONS) {
      for (const p of sec.paths) {
        if (pathname === p || pathname.startsWith(p + '/')) {
          if (p.length > bestLen) {
            best = sec;
            bestLen = p.length;
          }
        }
      }
    }
    return best;
  }, [pathname]);

  if (!activeSection) return null;
  if (HIDDEN_PATHS.has(pathname)) return null;

  // Collapsed: render a thin pill so the user can re-expand any time without
  // losing the section context. Single line, far less vertical real estate.
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        className="group mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-200/70 dark:border-emerald-500/20 midnight:border-emerald-400/20 classic:border-emerald-200/70 bg-emerald-50/70 dark:bg-emerald-950/30 midnight:bg-emerald-900/30 classic:bg-emerald-50/70 backdrop-blur-sm hover:border-emerald-500/40 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 transition-all shadow-sm"
        data-testid="quick-links-expand"
        aria-label={`Expand ${activeSection.title} quick links`}
      >
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-gradient-to-br from-emerald-500 to-amber-500 text-white">
          <Sparkles className="w-2.5 h-2.5" />
        </span>
        <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-emerald-700 dark:text-emerald-400">
          Quick Links · {activeSection.title}
        </span>
        <ChevronDown className="w-3 h-3 text-muted-foreground group-hover:text-emerald-600 dark:group-hover:text-emerald-400" />
      </button>
    );
  }

  return (
    <nav
      aria-label={`${activeSection.title} quick links`}
      className="relative mb-4 rounded-2xl border border-emerald-200/70 dark:border-emerald-500/20 midnight:border-emerald-400/20 classic:border-emerald-200/70 bg-gradient-to-br from-emerald-50 via-white to-amber-50/40 dark:from-emerald-950/40 dark:via-zinc-950 dark:to-amber-950/20 midnight:from-emerald-900/35 midnight:via-card midnight:to-amber-900/15 classic:from-emerald-50 classic:via-white classic:to-emerald-50/40 shadow-sm overflow-hidden"
      data-testid="quick-links"
    >
      {/* Soft sheen — adds the subtle "3D" depth without being a gimmick */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.08),transparent_60%)]" />
      <div className="relative z-10 p-3 sm:p-4">
        <div className="flex items-center gap-2 mb-2.5">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-gradient-to-br from-emerald-500 to-amber-500 text-white">
            <Sparkles className="w-3 h-3" />
          </span>
          <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-emerald-700 dark:text-emerald-400 midnight:text-emerald-300">
            Quick Links · {activeSection.title}
          </p>
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground midnight:text-emerald-200/80 hover:text-emerald-600 dark:hover:text-emerald-400 midnight:hover:text-emerald-200 hover:bg-emerald-50/50 dark:hover:bg-emerald-950/30 midnight:hover:bg-emerald-500/15 transition-colors"
            data-testid="quick-links-collapse"
            aria-label="Collapse quick links"
            title="Collapse — frees up vertical space"
          >
            Collapse <ChevronUp className="w-3 h-3" />
          </button>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="quick-links-tiles">
          {activeSection.items.map((item) => {
            const Icon = item.icon;
            const itemPath = item.path.split('?')[0];
            const itemQuery = item.path.includes('?') ? '?' + item.path.split('?')[1] : '';
            const isActive = pathname === itemPath && (!itemQuery || search === itemQuery);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'group relative flex items-center gap-2 px-3 py-2 rounded-xl border transition-all duration-200',
                  'bg-white dark:bg-zinc-900/70 midnight:bg-[hsl(222,28%,22%)] classic:bg-white backdrop-blur-sm',
                  // 3D-ish lift via shadow + translate. Active tile sits in a pressed state.
                  isActive
                    ? 'border-emerald-500/70 ring-2 ring-emerald-500/30 shadow-[inset_0_2px_6px_rgba(16,185,129,0.18)] text-emerald-700 dark:text-emerald-300 midnight:text-emerald-200'
                    : 'border-emerald-200/60 dark:border-white/10 midnight:border-emerald-400/30 classic:border-emerald-200/60 shadow-sm hover:shadow-lg hover:-translate-y-0.5 hover:border-emerald-500/40 text-zinc-700 dark:text-zinc-200 midnight:text-emerald-50',
                )}
                data-testid={`quick-link-${item.path.replace(/[/?=]/g, '-')}`}
              >
                <span className={cn(
                  'w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0',
                  isActive
                    ? 'bg-emerald-500 text-white'
                    : 'bg-gradient-to-br from-zinc-100 to-zinc-200 dark:from-zinc-800 dark:to-zinc-900 midnight:from-emerald-500/25 midnight:to-emerald-600/15 group-hover:from-emerald-100 group-hover:to-emerald-200 dark:group-hover:from-emerald-900/40 dark:group-hover:to-emerald-950/40 text-emerald-600 dark:text-emerald-400 midnight:text-emerald-200',
                )}>
                  <Icon className="w-3.5 h-3.5" />
                </span>
                <span className="text-xs font-medium whitespace-nowrap">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
